import { useMemo } from "react";
import { useGetCmpEventMemberAttendeeSuggestionsQuery } from "../../../../graphql/types";
import type { EventAttendeeSuggestion } from "./eventAttendeeSuggestions";

/**
 * Attendee Name suggestions for the selected member (IF11 EV7, Q12): the
 * member's previous attendees from all events and its profile contacts,
 * Primary Contact first. A non-member invoice has none.
 */
export const useEventMemberAttendeeSuggestions = ({
  chamberId,
  memberId,
}: {
  chamberId: string;
  memberId: string | null | undefined;
}): EventAttendeeSuggestion[] => {
  const normalizedMemberId = memberId?.trim() || "";
  const { data } = useGetCmpEventMemberAttendeeSuggestionsQuery({
    variables: { input: { chamberId, memberId: normalizedMemberId } },
    skip: !chamberId || !normalizedMemberId,
    fetchPolicy: "cache-and-network",
  });
  return useMemo(
    () =>
      normalizedMemberId
        ? (data?.getCmpEventMemberAttendeeSuggestions.suggestions || []).map(
            (suggestion, index) => ({
              email: suggestion.email,
              id: `member-attendee-suggestion-${index + 1}`,
              name: suggestion.name,
              phone: suggestion.phone || "",
              role: suggestion.role || null,
            }),
          )
        : [],
    [data, normalizedMemberId],
  );
};

/** Design attendeeCompanyOptions: the member company names, A to Z. */
export const getEventAttendeeCompanyOptions = (
  members: ReadonlyArray<{ billingSnapshot: { customerName: string } }>,
): string[] =>
  Array.from(
    new Set(
      members
        .map((member) => member.billingSnapshot.customerName.trim())
        .filter(Boolean),
    ),
  ).sort((first, second) => first.localeCompare(second));
